import config from '../../config';
import {AirtablePromotionItem} from '../../models/airtable';
import {isGiftCardsPromotion} from '../../utils/giftCards';

import {
  createFilterMembersOnlyPromotion,
  isActiveInStorePromotion,
  isActivePromotion,
  isCarouselPromotion,
  isDeepLinkOnlyPromotion,
  isFeedPromotion,
  isListedEcommerceOnlyPromotion,
} from './predicates';
import {DerivedPromotions, PromotionArms, PromotionsGate} from './types';

/**
 * Pure. Every collection except `drawnWinnersPromotions` derives from
 * [...live, ...winners] — literally what the legacy /content/promotions
 * expression was, so membership is bit-identical and does not depend on the
 * server routing each record to the correct arm (DIS-1567), and nothing
 * defends /winners carrying a NON-drawn record: it would miss
 * `activePromotions` and silently stop a completed order entering a live
 * competition (cartStore.ts:1228).
 */
export const derivePromotions = (arms: PromotionArms, gate: PromotionsGate): DerivedPromotions => {
  const filterMembersOnlyPromotion = createFilterMembersOnlyPromotion(gate);

  // TMP: 4048-Version with Gift Cards toggled off-------------------------
  // Applied to BOTH arms: the strip used to run once over the single response.
  const stripGiftCards = (items: AirtablePromotionItem[]) =>
    config.FF_IS_GIFT_CARDS_ENABLED === false
      ? items.filter(promotion => !isGiftCardsPromotion(promotion))
      : items;
  //-----------------------------------------------------------------------

  const livePromotions = stripGiftCards(arms.live);
  const winnersPromotions = stripGiftCards(arms.winners);

  const allPromotions = [...livePromotions, ...winnersPromotions];
  const basePromotions = allPromotions.filter(filterMembersOnlyPromotion);
  const activeInStorePromotion = basePromotions.filter(isActiveInStorePromotion);

  return {
    rawPromotions: allPromotions,
    promotions: activeInStorePromotion,
    activePromotions: basePromotions.filter(isActivePromotion),
    carouselData: activeInStorePromotion.filter(isCarouselPromotion),
    feedData: activeInStorePromotion.filter(isFeedPromotion),
    // Straight from /promotions/winners. No status inspection: retrieval no longer
    // depends on the client recognising an Airtable status value, and no date window
    // is applied — a drawn record is normally already past its end date (PR #387).
    // The one collection where arm identity is still load-bearing.
    drawnWinnersPromotions: winnersPromotions,
    // From `basePromotions`, not the raw response: this list is appended to Current
    // Promotions for pickup/delivery stores, so deriving it from the raw response
    // bypassed the members-only gate and showed members-only offers to logged-out
    // users (DIS-1564).
    listedECommerceOnlyPromotions: basePromotions.filter(isListedEcommerceOnlyPromotion),
    deepLinkOnlyPromotions: allPromotions.filter(isDeepLinkOnlyPromotion),
  };
};
